import React, { useState, useEffect } from 'react';
import { Download, File, Search, Upload, Eye, X } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { toast } from 'sonner';
import api from '../../utils/api';
import PDFViewer from '../ui/PDFViewer';

const ClientDocuments = () => {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [uploading, setUploading] = useState(false);
  const [previewFile, setPreviewFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  useEffect(() => {
    fetchFiles();
  }, []);

  const fetchFiles = async () => {
    try {
      const response = await api.get('/files');
      setFiles(response.data);
    } catch (error) {
      console.error('Failed to fetch files:', error);
      toast.error('Failed to load documents');
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      await api.post('/files/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('File uploaded successfully!');
      fetchFiles();
    } catch (error) {
      console.error('Failed to upload file:', error);
      toast.error('Failed to upload file');
    } finally {
      setUploading(false);
    }
  };

  const handleDownload = async (fileId, filename) => {
    try {
      const response = await api.get(`/files/${fileId}/download`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Download started');
    } catch (error) {
      console.error('Failed to download file:', error);
      toast.error('Failed to download file');
    }
  };

  const handlePreview = async (file) => {
    const canPreview = file.mime_type?.startsWith('image/') || 
                       file.mime_type === 'application/pdf';
    
    if (!canPreview) {
      toast.info('This file type cannot be previewed. Downloading instead...');
      handleDownload(file.id, file.filename);
      return;
    }

    setPreviewLoading(true);
    setPreviewFile(file);

    try {
      // For PDFs, use direct URL with token for iframe rendering
      if (file.mime_type === 'application/pdf') {
        const token = localStorage.getItem('token');
        const backendUrl = process.env.REACT_APP_BACKEND_URL;
        const directUrl = `${backendUrl}/api/files/${file.id}/preview?token=${token}`;
        setPreviewUrl(directUrl);
      } else {
        // For images, use blob approach
        const response = await api.get(`/files/${file.id}/preview`, {
          responseType: 'blob'
        });
        const blob = new Blob([response.data], { type: file.mime_type });
        const url = URL.createObjectURL(blob);
        setPreviewUrl(url);
      }
    } catch (error) {
      console.error('Failed to load preview:', error);
      toast.error('Failed to load preview');
      setPreviewFile(null);
    } finally {
      setPreviewLoading(false);
    }
  };

  const closePreview = () => {
    if (previewUrl && previewUrl.startsWith('blob:')) {
      URL.revokeObjectURL(previewUrl);
    }
    setPreviewFile(null);
    setPreviewUrl(null);
  };

  const filteredFiles = files.filter(file =>
    file.filename.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2" data-testid="documents-title">My Documents</h2>
          <p className="text-gray-600">Access and manage your files</p>
        </div>
        <div>
          <input
            type="file"
            id="client-file-upload"
            data-testid="client-file-upload-input"
            onChange={handleFileUpload}
            className="hidden"
            disabled={uploading}
          />
          <label htmlFor="client-file-upload">
            <Button
              data-testid="client-upload-button"
              as="span"
              disabled={uploading}
              className="bg-gradient-to-r from-teal-600 to-cyan-600 hover:from-teal-700 hover:to-cyan-700 text-white cursor-pointer"
            >
              <Upload className="w-4 h-4 mr-2" />
              {uploading ? 'Uploading...' : 'Upload File'}
            </Button>
          </label>
        </div>
      </div>

      {/* Search */}
      <div className="glass rounded-xl p-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
          <Input
            data-testid="search-documents-input"
            type="text"
            placeholder="Search documents..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
      </div>

      {/* Documents Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredFiles.length === 0 ? (
          <div className="col-span-full glass rounded-xl p-12 text-center">
            <File className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500">
              {searchTerm ? 'No documents found' : 'No documents available yet'}
            </p>
          </div>
        ) : (
          filteredFiles.map((file, index) => (
            <div
              key={file.id}
              className="glass rounded-xl p-6 hover-lift transition-all"
              data-testid={`document-card-${index}`}
            >
              <div className="flex items-start justify-between mb-4">
                <div className="w-12 h-12 bg-gradient-to-br from-teal-100 to-cyan-100 rounded-lg flex items-center justify-center">
                  <File className="w-6 h-6 text-teal-600" />
                </div>
                <div className="flex items-center gap-1">
                  {(file.mime_type?.startsWith('image/') || file.mime_type === 'application/pdf') && (
                    <Button
                      data-testid={`preview-button-${index}`}
                      size="sm"
                      variant="ghost"
                      onClick={() => handlePreview(file)}
                      className="text-gray-600 hover:text-blue-600"
                      title="Preview"
                    >
                      <Eye className="w-4 h-4" />
                    </Button>
                  )}
                  <Button
                    data-testid={`download-button-${index}`}
                    size="sm"
                    variant="ghost"
                    onClick={() => handleDownload(file.id, file.filename)}
                    className="text-gray-600 hover:text-teal-600"
                    title="Download"
                  >
                    <Download className="w-4 h-4" />
                  </Button>
                </div>
              </div>
              <h3 className="font-medium text-gray-900 mb-2 truncate" title={file.filename}>
                {file.filename}
              </h3>
              <p className="text-sm text-gray-500 mb-1">{formatFileSize(file.file_size)}</p>
              <p className="text-xs text-gray-400">
                {new Date(file.uploaded_at).toLocaleDateString()}
              </p>
            </div>
          ))
        )}
      </div>

      {/* Preview Modal */}
      <Dialog open={!!previewFile} onOpenChange={closePreview}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-hidden">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              <span className="truncate pr-4">{previewFile?.filename}</span>
              <Button
                size="sm"
                variant="outline"
                onClick={() => handleDownload(previewFile?.id, previewFile?.filename)}
              >
                <Download className="w-4 h-4 mr-2" />
                Download
              </Button>
            </DialogTitle>
          </DialogHeader>
          <div className="overflow-auto max-h-[calc(90vh-100px)]">
            {previewLoading ? (
              <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
              </div>
            ) : previewFile && previewUrl ? (
              <>
                {previewFile.mime_type?.startsWith('image/') ? (
                  <img 
                    src={previewUrl}
                    alt={previewFile.filename}
                    className="w-full h-auto"
                  />
                ) : previewFile.mime_type === 'application/pdf' ? (
                  <div className="w-full h-[650px]">
                    <PDFViewer 
                      url={previewUrl}
                      filename={previewFile.filename}
                      onDownload={() => handleDownload(previewFile.id, previewFile.filename)}
                    />
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <File className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-500 mb-4">Preview not available</p>
                    <Button onClick={() => handleDownload(previewFile.id, previewFile.filename)}>
                      <Download className="w-4 h-4 mr-2" />
                      Download File
                    </Button>
                  </div>
                )}
              </>
            ) : null}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ClientDocuments;
